import { isFeaturedVisualPrompt } from '@/constants/visualPrompts/featuredPromptIds';
import { MATTER_RELATED_STEP } from '@/constants/visualPrompts/matterStep';
import type { VisualPromptDefinition } from '@/types/visualPrompts/visualPrompt.types';

const REWRITE_TEMPLATE = `Rewrite the following firm communication for use in a law firm business context.

Focus: {{task}}
Audience: {{audience}}
Channel: {{context}}
Tone: {{tone}}
Constraints and standards: {{constraints}}

Rules:
- Keep the original meaning and do not add legal advice or new facts.
- Follow the firm's brand and communication standards where selected above.
- Use British English unless stated otherwise.
- Be appropriate for a professional services / law firm environment.
- Do not reference confidential matter details unless they appear in the source text.

Output format: {{output}}

If the output format asks for two versions, provide:
1) A short Teams/chat version (under 100 words unless constraints say otherwise)
2) A fuller email version

Otherwise follow the output format exactly. Return only the final content (no commentary).

{{sourceText}}`;

const EXPLAIN_TEMPLATE = `Explain {{topic}} to {{audience}} in a UK law firm context.

This relates to internal business systems, processes, or tools used by lawyers and business services staff.

Assume they know: {{known}}
Assume they do not know: {{unknown}}

Tone: {{tone}}
Output format: {{output}}

Use plain English suitable for a law firm. Avoid unnecessary technical jargon. Where relevant, note confidentiality, data handling, or compliance considerations at a high level (not legal advice).

{{sourceText}}`;

const MEETING_NOTES_TEMPLATE = `Turn the following meeting notes into a clear summary for a law firm audience (e.g. partnership, practice group, or business systems steering).

Context: {{context}}

Output format:
1. Brief summary
2. Key decisions
3. Action items (Owner, Task, Deadline)
4. Risks, dependencies, or blockers (including matter/client impact if noted)
5. Follow-up questions

Notes:
{{sourceText}}`;

const SYSTEM_ANNOUNCEMENT_TEMPLATE = `Draft an internal law firm announcement about a business system change.

Situation: {{task}}
Audience: {{audience}}
Tone: {{tone}}
Constraints: {{constraints}}

Include:
- What is changing and why (in plain language)
- When it happens and any downtime or cutover window
- What staff need to do (if anything)
- Where to get help (Service Desk / IT / Knowledge team — use placeholders if unknown)

Do not invent system names, dates, or contacts not provided in the notes below.
Follow firm brand and communication standards where selected.

Draft notes or bullet inputs:
{{sourceText}}`;

const FEEDBACK_TEMPLATE = `Analyse the following feedback about an internal law firm business system (e.g. document management, time recording, HR portal, finance, or collaboration tools).

Return a table with columns: theme, frequency, sentiment, impact on lawyers/staff, recommended action.

Constraints: {{constraints}}

Feedback:
{{sourceText}}`;

const GENERAL_TEMPLATE = `You are helping staff in a UK law firm with a task related to business systems, operations, or internal communications.

Task: {{task}}
Context: {{context}}
Audience: {{audience}}
Goal: {{goal}}
Tone: {{tone}}
Constraints: {{constraints}}
Output format: {{output}}

{{sourceText}}`;

const CREATIVE_TEMPLATE = `Generate practical ideas for improving adoption, communication, or workflow around law firm business systems.

Goal: {{goal}}
Audience: {{audience}}
Constraints: {{constraints}}

Output format: {{output}}

Ideas should be realistic for a law firm (confidentiality, busy lawyers, risk/compliance awareness, training burden). Provide numbered ideas with a one-line rationale each.`;

const SERVICE_DESK_TEMPLATE = `Turn the following notes into a clear Service Desk ticket description for an internal IT / business systems team in a UK law firm.

Impact: {{task}}
Who is affected: {{audience}}
Urgency / tone: {{tone}}
Constraints: {{constraints}}

Structure the ticket as:
1. Summary (one line, suitable for ticket title)
2. Description (what is happening, when it started, what the user was trying to do)
3. Impact (who cannot work, which systems or processes)
4. Steps already tried
5. Requested outcome

Use plain language so Service Desk and support staff can triage quickly. Do not invent system names, error codes, or dates not in the notes.

{{sourceText}}`;

const withMatterStep = (
  steps: VisualPromptDefinition['steps'],
): VisualPromptDefinition['steps'] => {
  const review = steps.filter((s) => s.type === 'review');
  const rest = steps.filter((s) => s.type !== 'review');
  const hasMatter = rest.some((s) => s.mapsTo === 'matterRelated');
  if (hasMatter) {
    return steps;
  }
  return [...rest.slice(0, 1), MATTER_RELATED_STEP, ...rest.slice(1), ...review];
};

const RAW_VISUAL_PROMPT_DEFINITIONS: VisualPromptDefinition[] = [
  {
    id: 'rewrite-message',
    title: 'Rewrite firm communication',
    description:
      'Polish emails, Teams posts, or intranet text for lawyers and business services—aligned to audience and brand.',
    category: 'writing',
    promptType: 'rewrite',
    version: 2,
    requiresContent: true,
    compileTemplate: REWRITE_TEMPLATE,
    steps: withMatterStep([
      {
        id: 'task',
        type: 'singleSelect',
        title: 'What do you want to improve?',
        mapsTo: 'task',
        required: true,
        options: [
          {
            id: 'clarity',
            label: 'Clarity',
            description: 'Easier for busy lawyers to scan',
          },
          {
            id: 'professional',
            label: 'More professional',
            description: 'Partner or client-appropriate tone',
          },
          { id: 'concise', label: 'More concise' },
          {
            id: 'change-comms',
            label: 'System change communication',
            description: 'Maintenance, rollout, or training',
          },
        ],
      },
      {
        id: 'audience',
        type: 'singleSelectPills',
        title: 'Who is this for?',
        mapsTo: 'audience',
        required: true,
        options: [
          { id: 'all-firm', label: 'All firm staff' },
          { id: 'lawyers', label: 'Lawyers & fee earners' },
          { id: 'business-services', label: 'Business services (HR, Finance, IT)' },
          { id: 'partners', label: 'Partners / leadership' },
          { id: 'secretarial', label: 'Secretarial & PA teams' },
        ],
      },
      {
        id: 'context',
        type: 'singleSelectPills',
        title: 'Channel',
        mapsTo: 'context',
        required: true,
        options: [
          { id: 'email', label: 'Email' },
          { id: 'teams', label: 'Teams / chat' },
          { id: 'intranet', label: 'Intranet / knowledge article' },
          { id: 'service-desk', label: 'Service Desk ticket update' },
        ],
      },
      {
        id: 'tone',
        type: 'singleSelectPills',
        title: 'Tone',
        mapsTo: 'tone',
        required: true,
        options: [
          { id: 'friendly-professional', label: 'Friendly and professional' },
          { id: 'direct', label: 'Direct and concise' },
          { id: 'calm', label: 'Calm and reassuring' },
          { id: 'formal', label: 'Formal (partnership-level)' },
        ],
      },
      {
        id: 'constraints',
        type: 'multiSelect',
        title: 'Standards and rules',
        mapsTo: 'constraints',
        options: [
          { id: 'brand', label: 'Follow brand guidelines' },
          { id: 'style-guide', label: 'Follow firm style guide / terminology' },
          { id: 'british', label: 'British English' },
          { id: 'under-150', label: 'Keep under 150 words' },
          { id: 'no-jargon', label: 'Avoid IT jargon' },
          { id: 'no-emoji', label: 'No emojis' },
          { id: 'confidentiality', label: 'Avoid matter-specific or client-identifying detail' },
          { id: 'plain-english', label: 'Plain English (non-technical readers)' },
        ],
      },
      {
        id: 'output',
        type: 'singleSelectPills',
        title: 'Output format',
        mapsTo: 'output',
        required: true,
        options: [
          { id: 'single', label: 'Single polished version' },
          {
            id: 'dual',
            label: 'Teams short version + email long version',
          },
          { id: 'teams-only', label: 'Teams / chat only (short)' },
          { id: 'email-only', label: 'Email only' },
        ],
      },
      {
        id: 'source',
        type: 'textarea',
        title: 'Paste your text',
        description: 'The message you want rewritten.',
        mapsTo: 'sourceText',
        required: true,
        placeholder: 'Paste the draft communication here…',
      },
      { id: 'review', type: 'review', title: 'Review', mapsTo: '_review' },
    ]),
  },
  {
    id: 'explain-topic',
    title: 'Explain a system or process',
    description:
      'Explain tools, workflows, or policies to lawyers and business services staff.',
    category: 'systems',
    promptType: 'explanation',
    version: 2,
    compileTemplate: EXPLAIN_TEMPLATE,
    steps: withMatterStep([
      {
        id: 'topic',
        type: 'text',
        title: 'What should be explained?',
        mapsTo: 'topic',
        required: true,
        placeholder: 'e.g. Why we use single sign-on for matter systems',
      },
      {
        id: 'audience',
        type: 'singleSelectPills',
        title: 'Audience',
        mapsTo: 'audience',
        required: true,
        options: [
          { id: 'lawyers', label: 'Lawyers & associates' },
          { id: 'business-services', label: 'Business services' },
          { id: 'secretarial', label: 'Secretarial / document services' },
          { id: 'partners', label: 'Partners (high-level)' },
          { id: 'new-starters', label: 'New starters' },
        ],
      },
      {
        id: 'known',
        type: 'text',
        title: 'What they already know',
        mapsTo: 'known',
        placeholder: 'e.g. They use the document management system daily',
      },
      {
        id: 'unknown',
        type: 'text',
        title: 'What they probably do not know',
        mapsTo: 'unknown',
        placeholder: 'e.g. How authentication links systems together',
      },
      {
        id: 'tone',
        type: 'singleSelectPills',
        title: 'Tone',
        mapsTo: 'tone',
        required: true,
        options: [
          { id: 'plain', label: 'Plain English' },
          { id: 'supportive', label: 'Supportive / training-style' },
          { id: 'concise', label: 'Concise (busy lawyers)' },
        ],
      },
      {
        id: 'output',
        type: 'singleSelectPills',
        title: 'Output format',
        mapsTo: 'output',
        required: true,
        options: [
          { id: 'short', label: 'Short summary' },
          { id: 'sections', label: 'With headings' },
          { id: 'bullets', label: 'Bullet points' },
          { id: 'faq', label: 'Short FAQ' },
        ],
      },
      { id: 'review', type: 'review', title: 'Review', mapsTo: '_review' },
    ]),
  },
  {
    id: 'meeting-notes',
    title: 'Summarise meeting notes',
    description:
      'Structure notes from IT steering, practice meetings, or change workshops into actions and decisions.',
    category: 'meetings',
    promptType: 'meetingNotes',
    version: 2,
    requiresContent: true,
    compileTemplate: MEETING_NOTES_TEMPLATE,
    steps: withMatterStep([
      {
        id: 'output-preset',
        type: 'singleSelect',
        title: 'Meeting type',
        mapsTo: 'context',
        options: [
          { id: 'systems-steering', label: 'Business systems / IT steering' },
          { id: 'change-project', label: 'Change project or rollout' },
          { id: 'practice', label: 'Practice group / department' },
          { id: 'executive', label: 'Executive / partnership brief' },
        ],
      },
      {
        id: 'notes',
        type: 'textarea',
        title: 'Paste meeting notes',
        mapsTo: 'sourceText',
        required: true,
        placeholder: 'Paste notes here…',
      },
      { id: 'review', type: 'review', title: 'Review', mapsTo: '_review' },
    ]),
  },
  {
    id: 'announce-system-change',
    title: 'Draft system change announcement',
    description:
      'Create a clear internal announcement for downtime, upgrades, or new firm systems.',
    category: 'systems',
    promptType: 'instruction',
    version: 1,
    requiresContent: true,
    compileTemplate: SYSTEM_ANNOUNCEMENT_TEMPLATE,
    steps: withMatterStep([
      {
        id: 'task',
        type: 'singleSelect',
        title: 'Type of change',
        mapsTo: 'task',
        required: true,
        options: [
          { id: 'downtime', label: 'Planned downtime / maintenance' },
          { id: 'upgrade', label: 'Upgrade or new feature' },
          { id: 'new-system', label: 'New system go-live' },
          { id: 'process', label: 'Process or policy change in a system' },
        ],
      },
      {
        id: 'audience',
        type: 'singleSelectPills',
        title: 'Audience',
        mapsTo: 'audience',
        required: true,
        options: [
          { id: 'all-firm', label: 'All firm staff' },
          { id: 'lawyers', label: 'Lawyers only' },
          { id: 'business-services', label: 'Business services' },
          { id: 'affected', label: 'Affected offices / groups only' },
        ],
      },
      {
        id: 'tone',
        type: 'singleSelectPills',
        title: 'Tone',
        mapsTo: 'tone',
        required: true,
        options: [
          { id: 'calm', label: 'Calm and reassuring' },
          { id: 'direct', label: 'Direct and factual' },
          { id: 'formal', label: 'Formal' },
        ],
      },
      {
        id: 'constraints',
        type: 'multiSelect',
        title: 'Include',
        mapsTo: 'constraints',
        options: [
          { id: 'brand', label: 'Follow brand guidelines' },
          { id: 'british', label: 'British English' },
          { id: 'plain-english', label: 'Plain English' },
          { id: 'timeline', label: 'Clear date/time (if provided in notes)' },
          { id: 'actions', label: 'What staff must do before/after' },
          { id: 'help', label: 'Where to get help' },
        ],
      },
      {
        id: 'notes',
        type: 'textarea',
        title: 'Facts and bullet points',
        description: 'Dates, systems affected, impact—do not invent missing details.',
        mapsTo: 'sourceText',
        required: true,
        placeholder: 'e.g. Document management unavailable Sat 6pm–8pm…',
      },
      { id: 'review', type: 'review', title: 'Review', mapsTo: '_review' },
    ]),
  },
  {
    id: 'service-desk-ticket',
    title: 'Service Desk ticket from notes',
    description:
      'Turn rough notes into a clear ticket IT can triage—symptoms, impact, and what you need.',
    category: 'systems',
    promptType: 'instruction',
    version: 1,
    requiresContent: true,
    compileTemplate: SERVICE_DESK_TEMPLATE,
    steps: withMatterStep([
      {
        id: 'task',
        type: 'singleSelect',
        title: 'What is the impact?',
        mapsTo: 'task',
        required: true,
        options: [
          { id: 'blocked', label: 'I cannot work (blocked)' },
          { id: 'degraded', label: 'Work is slower / degraded' },
          { id: 'question', label: 'Question or how-to' },
          { id: 'request', label: 'Access or new request' },
        ],
      },
      {
        id: 'audience',
        type: 'singleSelectPills',
        title: 'Who is affected?',
        mapsTo: 'audience',
        required: true,
        options: [
          { id: 'me', label: 'Just me' },
          { id: 'team', label: 'My team' },
          { id: 'practice', label: 'Whole practice group' },
          { id: 'firm', label: 'Firm-wide' },
        ],
      },
      {
        id: 'tone',
        type: 'singleSelectPills',
        title: 'Urgency',
        mapsTo: 'tone',
        required: true,
        options: [
          { id: 'urgent', label: 'Urgent — needs today' },
          { id: 'normal', label: 'Normal' },
          { id: 'low', label: 'Low — when possible' },
        ],
      },
      {
        id: 'constraints',
        type: 'multiSelect',
        title: 'Include in ticket',
        mapsTo: 'constraints',
        options: [
          { id: 'screenshots', label: 'Mention screenshots attached separately' },
          { id: 'error-text', label: 'Include exact error text if provided' },
          { id: 'system-name', label: 'Name the system if known' },
          { id: 'no-invent', label: 'Do not invent details' },
        ],
      },
      {
        id: 'notes',
        type: 'textarea',
        title: 'Describe the issue',
        description: 'What happened, what you tried, any error messages.',
        mapsTo: 'sourceText',
        required: true,
        placeholder: 'e.g. Cannot log into document management since this morning…',
      },
      { id: 'review', type: 'review', title: 'Review', mapsTo: '_review' },
    ]),
  },
  {
    id: 'summarise-document',
    title: 'Summarise for internal use',
    description:
      'Summarise policies, requirements, or long emails for lawyers or business services.',
    category: 'analysis',
    promptType: 'instruction',
    version: 2,
    compileTemplate: `Summarise the following content for internal use in a UK law firm.

Audience: {{audience}}
Length: {{task}}
Tone: {{tone}}
Purpose: {{context}}

Do not provide legal advice. Flag if content appears to contain client-identifying or matter-confidential information.

Note: Attach documents in the chat after starting if needed.

{{sourceText}}`,
    steps: withMatterStep([
      {
        id: 'length',
        type: 'singleSelectPills',
        title: 'Summary length',
        mapsTo: 'task',
        required: true,
        options: [
          { id: 'brief', label: 'Brief (3–5 bullets)' },
          { id: 'medium', label: 'Medium paragraph' },
          { id: 'detailed', label: 'Detailed with sections' },
        ],
      },
      {
        id: 'audience',
        type: 'singleSelectPills',
        title: 'Audience',
        mapsTo: 'audience',
        required: true,
        options: [
          { id: 'lawyers', label: 'Lawyers' },
          { id: 'business-services', label: 'Business services' },
          { id: 'it-ops', label: 'IT / systems team' },
          { id: 'leadership', label: 'Leadership' },
        ],
      },
      {
        id: 'context',
        type: 'singleSelectPills',
        title: 'Purpose',
        mapsTo: 'context',
        options: [
          { id: 'decision', label: 'Support a decision' },
          { id: 'training', label: 'Training / onboarding' },
          { id: 'vendor', label: 'Vendor or RFP review' },
          { id: 'policy', label: 'Policy or compliance overview' },
        ],
      },
      {
        id: 'tone',
        type: 'singleSelectPills',
        title: 'Tone',
        mapsTo: 'tone',
        required: true,
        options: [
          { id: 'neutral', label: 'Neutral' },
          { id: 'professional', label: 'Professional' },
          { id: 'plain', label: 'Plain English' },
        ],
      },
      {
        id: 'content',
        type: 'textarea',
        title: 'Paste content (optional)',
        mapsTo: 'sourceText',
        placeholder: 'Or attach documents after starting the chat',
      },
      { id: 'review', type: 'review', title: 'Review', mapsTo: '_review' },
    ]),
  },
  {
    id: 'feedback-analysis',
    title: 'Analyse system feedback',
    description:
      'Turn feedback on firm systems (DMS, time recording, portals) into themes and actions.',
    category: 'systems',
    promptType: 'structured',
    version: 2,
    requiresContent: true,
    compileTemplate: FEEDBACK_TEMPLATE,
    steps: withMatterStep([
      {
        id: 'constraints',
        type: 'multiSelect',
        title: 'Analysis rules',
        mapsTo: 'constraints',
        options: [
          { id: 'quotes', label: 'Include example quotes (anonymised)' },
          { id: 'priority', label: 'Rank by priority for IT/change team' },
          { id: 'no-invent', label: 'Do not invent details' },
          { id: 'lawyer-impact', label: 'Highlight impact on fee earners' },
          { id: 'compliance', label: 'Flag confidentiality or compliance mentions' },
        ],
      },
      {
        id: 'feedback',
        type: 'textarea',
        title: 'Paste feedback',
        mapsTo: 'sourceText',
        required: true,
        placeholder: 'Survey comments, Service Desk themes, workshop notes…',
      },
      { id: 'review', type: 'review', title: 'Review', mapsTo: '_review' },
    ]),
  },
  {
    id: 'general-purpose',
    title: 'Business systems brief',
    description:
      'Build a structured brief for any law firm systems, ops, or internal comms task.',
    category: 'general',
    promptType: 'instruction',
    version: 2,
    compileTemplate: GENERAL_TEMPLATE,
    steps: withMatterStep([
      {
        id: 'task',
        type: 'text',
        title: 'Task',
        description: 'What do you need help with?',
        mapsTo: 'task',
        required: true,
        placeholder: 'e.g. Draft FAQ for new time recording workflow',
      },
      {
        id: 'context',
        type: 'text',
        title: 'Context',
        mapsTo: 'context',
        placeholder: 'System, department, rollout phase, constraints',
      },
      {
        id: 'audience',
        type: 'singleSelectPills',
        title: 'Audience',
        mapsTo: 'audience',
        required: true,
        options: [
          { id: 'lawyers', label: 'Lawyers' },
          { id: 'business-services', label: 'Business services' },
          { id: 'it-ops', label: 'IT / systems team' },
          { id: 'leadership', label: 'Leadership' },
        ],
      },
      {
        id: 'goal',
        type: 'text',
        title: 'Goal',
        mapsTo: 'goal',
        placeholder: 'What does success look like?',
      },
      {
        id: 'tone',
        type: 'singleSelectPills',
        title: 'Tone',
        mapsTo: 'tone',
        options: [
          { id: 'professional', label: 'Professional' },
          { id: 'supportive', label: 'Supportive / training' },
          { id: 'direct', label: 'Direct' },
        ],
      },
      {
        id: 'constraints',
        type: 'multiSelect',
        title: 'Constraints',
        mapsTo: 'constraints',
        options: [
          { id: 'brand', label: 'Follow brand guidelines' },
          { id: 'british', label: 'British English' },
          { id: 'short', label: 'Keep it brief' },
          { id: 'no-jargon', label: 'Avoid IT jargon' },
          { id: 'confidentiality', label: 'No client/matter-identifying content' },
        ],
      },
      {
        id: 'output',
        type: 'singleSelectPills',
        title: 'Output format',
        mapsTo: 'output',
        options: [
          { id: 'prose', label: 'Prose' },
          { id: 'bullets', label: 'Bullet points' },
          { id: 'table', label: 'Table' },
          { id: 'faq', label: 'FAQ' },
        ],
      },
      {
        id: 'source',
        type: 'textarea',
        title: 'Reference material (optional)',
        mapsTo: 'sourceText',
      },
      { id: 'review', type: 'review', title: 'Review', mapsTo: '_review' },
    ]),
  },
  {
    id: 'creative-brainstorm',
    title: 'Ideas for adoption & comms',
    description:
      'Brainstorm ways to improve rollout, training, or communication for firm systems.',
    category: 'general',
    promptType: 'creative',
    version: 2,
    compileTemplate: CREATIVE_TEMPLATE,
    steps: [
      {
        id: 'goal',
        type: 'text',
        title: 'Goal',
        mapsTo: 'goal',
        required: true,
        placeholder: 'e.g. Increase adoption of electronic matter opening',
      },
      {
        id: 'audience',
        type: 'singleSelectPills',
        title: 'Primary audience',
        mapsTo: 'audience',
        required: true,
        options: [
          { id: 'lawyers', label: 'Lawyers' },
          { id: 'business-services', label: 'Business services' },
          { id: 'all-firm', label: 'All firm staff' },
          { id: 'change-team', label: 'IT / change team (internal)' },
        ],
      },
      {
        id: 'constraints',
        type: 'multiSelect',
        title: 'Constraints',
        mapsTo: 'constraints',
        options: [
          { id: 'ten', label: 'Provide 10 ideas' },
          { id: 'low-burden', label: 'Low burden on fee earners' },
          { id: 'compliance', label: 'Respect confidentiality / risk culture' },
          { id: 'brand', label: 'Align with firm brand' },
          { id: 'quick-wins', label: 'Include quick wins and longer-term items' },
        ],
      },
      {
        id: 'output',
        type: 'singleSelectPills',
        title: 'Output format',
        mapsTo: 'output',
        required: true,
        options: [
          { id: 'numbered', label: 'Numbered list with rationale' },
          { id: 'grouped', label: 'Grouped by theme (comms, training, process)' },
        ],
      },
      { id: 'review', type: 'review', title: 'Review', mapsTo: '_review' },
    ],
  },
];

export const MOCK_VISUAL_PROMPT_DEFINITIONS: VisualPromptDefinition[] =
  RAW_VISUAL_PROMPT_DEFINITIONS.map((definition) => ({
    ...definition,
    isFeatured: isFeaturedVisualPrompt(definition.id),
  }));
